import {
  UserProfile,
  ClientPageMapping,
  AdvancedFilters,
  GetClientPageMappingsQuery,
  GetUsersByEmailAddressQuery,
} from 'api';
import React, { ChangeEvent, HTMLAttributes } from 'react';
import { CompaniesFilter, PeopleFilter } from 'features/insights/types';
import { Checkbox, Chip, Grid } from '@mui/material';
import { UserAvatar } from 'components';
import { UseQueryResult } from 'react-query';
import { AutocompleteGetTagProps } from '@mui/material/useAutocomplete';
import CloseIcon from '@mui/icons-material/Close';

/**
 * AdvancedFilterInput Utils - START
 */
interface FilterSetters {
  tempFilter: PeopleFilter | CompaniesFilter;
  setTempFilter: (filter: PeopleFilter | CompaniesFilter) => void;
  setSearchFilterField: (searchTerm: string) => void;
}

function renderUsersOptionLabel(option: UserProfile) {
  return option.email_address || option;
}

function renderCompanyPagesOptionLabel(option: ClientPageMapping) {
  return option.name || option;
}

function handleOnChangeUsers(
  event: ChangeEvent,
  nextValue: [UserProfile],
  filterSetters: FilterSetters
) {
  const users = nextValue.map((user_info: string | UserProfile): string => {
    if (typeof user_info === 'string') {
      return user_info;
    }
    return user_info.email_address;
  });

  filterSetters.setTempFilter({
    ...filterSetters.tempFilter,
    users: users,
  });

  filterSetters.setSearchFilterField('');
}

function handleOnChangeCompanyPages(
  event: ChangeEvent,
  nextValue: [ClientPageMapping],
  filterSetters: FilterSetters
) {
  const pages: string[] = nextValue.map((clientPageMapping: string | ClientPageMapping) => {
    if (typeof clientPageMapping === 'string') {
      return clientPageMapping;
    }
    return clientPageMapping.name;
  });

  filterSetters.setTempFilter({
    ...filterSetters.tempFilter,
    company_pages: pages,
  });

  filterSetters.setSearchFilterField('');
}

function handleOnChangePosts(
  event: ChangeEvent,
  nextValue: [string],
  filterSetters: FilterSetters
) {
  filterSetters.setTempFilter({
    ...filterSetters.tempFilter,
    posts: nextValue,
  });
  filterSetters.setSearchFilterField('');
}

function renderUsersOption(
  props: HTMLAttributes<HTMLLIElement>,
  option: UserProfile,
  selected: boolean,
  icon: JSX.Element,
  checkedIcon: JSX.Element
) {
  return (
    <Grid component="li" container alignItems="center" {...props}>
      <Grid item xs={2}>
        <UserAvatar user={option} />
      </Grid>
      <Grid item xs>
        {option.email_address}
      </Grid>
      <Grid>
        <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
      </Grid>
    </Grid>
  );
}

function renderCompanyPagesOption(
  props: HTMLAttributes<HTMLLIElement>,
  option: ClientPageMapping,
  selected: boolean,
  icon: JSX.Element,
  checkedIcon: JSX.Element
) {
  return (
    <Grid component="li" container alignItems="center" {...props}>
      <Grid item xs>
        {option.name}
      </Grid>
      <Grid>
        <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
      </Grid>
    </Grid>
  );
}

function renderTags(value: string[], getTagProps: AutocompleteGetTagProps) {
  return value.map((item: string, index: number) => (
    <Chip key={index} label={item} deleteIcon={<CloseIcon />} {...getTagProps({ index })} />
  ));
}

function getUsersOptions(queryOptionsUsers: UseQueryResult<GetUsersByEmailAddressQuery>) {
  return queryOptionsUsers?.data ? queryOptionsUsers.data.getUsersByEmailAddress : [];
}

function getCompanyPagesOptions(
  queryOptionsCompanyPages: UseQueryResult<GetClientPageMappingsQuery>
) {
  return queryOptionsCompanyPages?.data ? queryOptionsCompanyPages.data.getClientPageMappings : [];
}

function getSelectedOptions(currentFilters: string[]) {
  return currentFilters || [];
}

function isOptionEqualToValueUsers(option: UserProfile, value: string) {
  return option.email_address === value;
}

function isOptionEqualToValueCompanyPages(option: ClientPageMapping, value: string) {
  return option.name === value;
}

export const AdvancedFilterFunctions = {
  [AdvancedFilters.Users]: {
    renderOption: renderUsersOption,
    renderOptionLabel: renderUsersOptionLabel,
    handleOnChange: handleOnChangeUsers,
    getOptions: getUsersOptions,
    getSelectedOptions: getSelectedOptions,
    renderTags: renderTags,
    isOptionEqualToValue: isOptionEqualToValueUsers,
  },
  [AdvancedFilters.CompanyPages]: {
    renderOption: renderCompanyPagesOption,
    renderOptionLabel: renderCompanyPagesOptionLabel,
    handleOnChange: handleOnChangeCompanyPages,
    getOptions: getCompanyPagesOptions,
    getSelectedOptions: getSelectedOptions,
    renderTags: renderTags,
    isOptionEqualToValue: isOptionEqualToValueCompanyPages,
  },
  [AdvancedFilters.Posts]: {
    renderOption: (): null => null,
    renderOptionLabel: (): null => null,
    handleOnChange: handleOnChangePosts,
    getOptions: (): null[] => [],
    getSelectedOptions: getSelectedOptions,
    renderTags: renderTags,
    isOptionEqualToValue: (): null => null,
  },
};

/**
 * AdvancedFilterInput Utils - END
 */
